import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, Globe, Menu, X, ChevronDown, ChevronRight, Clock, Home, Info, Briefcase, Heart, BarChart2, Shield, MapPin, Users } from 'lucide-react';
import { companyInfo, navLinks, aboutDropdown } from '../../data/mockData';
import { useLanguage } from '../../contexts/LanguageContext';

const ClockComponent = React.memo(({ t, companyInfo, selectedTimezone, setSelectedTimezone, currentTime, timezoneDropdownOpen, setTimezoneDropdownOpen }) => {
  return (
    <div className="relative">
      <button
        onClick={() => setTimezoneDropdownOpen(!timezoneDropdownOpen)}
        className="flex items-center gap-1.5 text-[11px] text-white/90 hover:text-white transition-colors duration-200 py-1 px-2 bg-white/10 rounded hover:bg-white/20"
      >
        {/* Clock Icon */}
        <div className="relative w-4 h-4 rounded-full bg-gradient-to-br from-[#20A99E] to-[#004a8b] flex items-center justify-center">
          <div
            className="absolute w-px h-1 bg-white rounded-full origin-bottom"
            style={{
              transform: `rotate(${(currentTime.getHours() % 12) * 30 + currentTime.getMinutes() * 0.5}deg)`,
              bottom: '50%',
              left: 'calc(50% - 0.5px)'
            }}
          />
          <div
            className="absolute w-px h-1.5 bg-cyan-300 rounded-full origin-bottom"
            style={{
              transform: `rotate(${currentTime.getMinutes() * 6}deg)`,
              bottom: '50%',
              left: 'calc(50% - 0.5px)'
            }}
          />
          <div className="absolute w-0.5 h-0.5 rounded-full bg-white" />
        </div>
        <span className="font-medium whitespace-nowrap">
          {currentTime.toLocaleDateString('en-US', {
            timeZone: selectedTimezone,
            weekday: 'short',
            month: 'short',
            day: 'numeric'
          })}
          {' | '}
          {currentTime.toLocaleTimeString('en-US', {
            timeZone: selectedTimezone,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
          })}
        </span>
        <ChevronDown
          size={10}
          className={`transition-transform duration-200 ${timezoneDropdownOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Timezone Dropdown Menu - Opens DOWNWARD */}
      {timezoneDropdownOpen && (
        <div className="absolute top-full left-0 mt-1 w-52 bg-white shadow-2xl rounded-lg py-1 z-[200] border border-gray-200 max-h-64 overflow-y-auto">
          <div className="px-3 py-1.5 border-b border-gray-100 sticky top-0 bg-white">
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Select Timezone</span>
          </div>
          {[
            { zone: 'Asia/Colombo', label: 'Sri Lanka', flag: '🇱🇰' },
            { zone: 'Asia/Kolkata', label: 'India', flag: '🇮🇳' },
            { zone: 'Asia/Dubai', label: 'UAE', flag: '🇦🇪' },
            { zone: 'Asia/Singapore', label: 'Singapore', flag: '🇸🇬' },
            { zone: 'Asia/Tokyo', label: 'Japan', flag: '🇯🇵' },
            { zone: 'Asia/Shanghai', label: 'China', flag: '🇨🇳' },
            { zone: 'Asia/Hong_Kong', label: 'Hong Kong', flag: '🇭🇰' },
            { zone: 'Europe/London', label: 'UK', flag: '🇬🇧' },
            { zone: 'Europe/Paris', label: 'France', flag: '🇫🇷' },
            { zone: 'Europe/Berlin', label: 'Germany', flag: '🇩🇪' },
            { zone: 'America/New_York', label: 'USA (East)', flag: '🇺🇸' },
            { zone: 'America/Los_Angeles', label: 'USA (West)', flag: '🇺🇸' },
            { zone: 'America/Chicago', label: 'USA (Central)', flag: '🇺🇸' },
            { zone: 'America/Toronto', label: 'Canada', flag: '🇨🇦' },
            { zone: 'Australia/Sydney', label: 'Australia', flag: '🇦🇺' },
            { zone: 'Pacific/Auckland', label: 'New Zealand', flag: '🇳🇿' },
          ].map((tz) => (
            <button
              key={tz.zone}
              onClick={() => {
                setSelectedTimezone(tz.zone);
                setTimezoneDropdownOpen(false);
              }}
              className={`w-full flex items-center justify-between px-3 py-2 text-xs transition-colors duration-150 ${selectedTimezone === tz.zone
                ? 'bg-[#20A99E]/10 text-[#20A99E]'
                : 'text-gray-700 hover:bg-gray-50'
                }`}
            >
              <div className="flex items-center space-x-2">
                <span>{tz.flag}</span>
                <span className="font-medium">{tz.label}</span>
              </div>
              <span className="font-medium text-gray-600">
                {currentTime.toLocaleTimeString('en-US', {
                  timeZone: tz.zone,
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true
                })}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

const Header = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
    const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [selectedTimezone, setSelectedTimezone] = useState('Asia/Colombo');
    const [timezoneDropdownOpen, setTimezoneDropdownOpen] = useState(false);
    const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
    const { language, setLanguage, t, languageNames, availableLanguages } = useLanguage();
    const location = useLocation();

  const dropdownTimeoutRef = useRef(null);
  const aboutRef = useRef(null);
  const productsRef = useRef(null);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Handle scroll - hide top bar when scrolled
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (aboutRef.current && !aboutRef.current.contains(event.target)) {
        if (activeDropdown === 'about') setActiveDropdown(null);
      }
      if (productsRef.current && !productsRef.current.contains(event.target)) {
        if (activeDropdown === 'products') setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeDropdown]);

  // Close dropdowns when route changes
  useEffect(() => {
    setActiveDropdown(null);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  // Dropdown hover handlers with delay
  const handleMouseEnter = (dropdownName) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setActiveDropdown(dropdownName);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  };

  // Dynamic styles for transparent/solid header
  const isTransparent = !isScrolled;
  const mainHeaderBgClass = isScrolled
    ? 'bg-white shadow-lg border-b border-gray-100'
    : 'bg-white/80 backdrop-blur-md shadow-md';
  const navTextColorClass = 'text-gray-700';

  // Translated navigation items
  const translatedNavLinks = [
    { label: t('nav.home'), path: "/" },
    { label: t('nav.about'), path: "/about", isAbout: true },
    {
      label: t('nav.products'),
      path: "/products",
      submenu: [
        { label: t('productsDropdown.leasingLoans'), path: "/products/leasing-loans" },
        { label: t('productsDropdown.goldLoan'), path: "/products/gold-cash" },
      ]
    },
    { label: t('nav.news'), path: "/news" },
    { label: t('nav.branches'), path: "/branches" },
    { label: t('nav.contact'), path: "/contact" },
  ];

  return (
    <header className="w-full fixed top-0 z-[100]">
      {/* Top Bar - Hidden when scrolled */}
      <div
        className={`hidden md:block transition-all duration-300 ease-in-out ${isScrolled ? 'max-h-0 opacity-0 pointer-events-none' : 'max-h-12 opacity-100'
          }`}
        style={{ backgroundColor: '#0a1628' }}
      >
        <div className="max-w-7xl mx-auto px-4 py-1">
          <div className="flex items-center justify-between">
            {/* Contact Info */}
            <div className="flex items-center space-x-5 text-xs text-white/90">
              <a
                href={`tel:${companyInfo.phone}`}
                className="flex items-center hover:text-[#20A99E] transition-colors duration-200"
              >
                <Phone size={14} className="mr-2" />
                <span className="hidden lg:inline">Phone:</span> {companyInfo.phone}
              </a>
              <a
                href={`mailto:${companyInfo.email}`}
                className="flex items-center hover:text-[#20A99E] transition-colors duration-200"
              >
                <Mail size={14} className="mr-2" />
                <span className="hidden lg:inline">Email:</span> {companyInfo.email}
              </a>
            </div>

            {/* Timezone Selector with Real-time Clock */}
            <div className="flex items-center space-x-3">
              {/* Timezone Dropdown */}
                <ClockComponent 
                  t={t}
                  companyInfo={companyInfo}
                  selectedTimezone={selectedTimezone}
                  setSelectedTimezone={setSelectedTimezone}
                  currentTime={currentTime}
                  timezoneDropdownOpen={timezoneDropdownOpen}
                  setTimezoneDropdownOpen={setTimezoneDropdownOpen}
                />
            </div>
          </div>
        </div>
      </div>

      {/* Main Header - Always visible, sticks to top when scrolled */}
      <div className={`${mainHeaderBgClass} transition-all duration-300`}>
        <div className="max-w-7xl mx-auto px-4 py-1">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img
                src="/logo.png"
                alt="Pride Path Commercial (pvt) ltd"
                className={`w-auto drop-shadow-md hover:drop-shadow-lg transition-all duration-300 brightness-110 saturate-125 object-contain ${
                  isScrolled ? 'h-10 md:h-12' : 'h-14 md:h-20'
                }`}
                style={{
                  filter: isScrolled 
                    ? 'brightness(1.1) saturate(1.25)' 
                    : 'brightness(1.1) saturate(1.25) drop-shadow(0 0 8px rgba(0, 102, 179, 0.3))',
                  imageRendering: 'auto'
                }}
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1 xl:space-x-2">
              {translatedNavLinks.map((link, index) => (
                <div key={index} className="relative">
                  {link.submenu ? (
                    <div
                      className="relative"
                      ref={productsRef}
                      onMouseEnter={() => handleMouseEnter('products')}
                      onMouseLeave={handleMouseLeave}
                    >
                      <Link
                        to={link.path}
                        className={`flex items-center text-sm font-semibold tracking-wide uppercase py-3 px-3 rounded-md transition-all duration-200 ${location.pathname.startsWith(link.path)
                          ? 'text-[#20A99E] bg-[#20A99E]/10'
                          : `${navTextColorClass} hover:text-[#20A99E] ${isScrolled ? 'hover:bg-[#20A99E]/5' : 'hover:bg-white/10'}`
                          }`}
                      >
                        {link.label}
                        <ChevronDown
                          size={14}
                          className={`ml-1 transition-transform duration-200 ${activeDropdown === 'products' ? 'rotate-180' : ''}`}
                        />
                      </Link>

                      {/* Products Mega Menu */}
                      <div
                        className={`absolute top-full left-0 mt-1 w-80 bg-white shadow-2xl rounded-lg py-3 z-[110] border border-gray-100 transition-all duration-200 origin-top ${activeDropdown === 'products'
                          ? 'opacity-100 transform scale-100 translate-y-0'
                          : 'opacity-0 transform scale-95 -translate-y-2 pointer-events-none'
                          }`}
                      >
                        <div className="px-4 pb-2 mb-2 border-b border-gray-100">
                          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                            {t('productsDropdown.title')}
                          </span>
                        </div>
                        {link.submenu.map((item, idx) => (
                          <Link
                            key={idx}
                            to={item.path}
                            className="block px-4 py-3 text-sm text-gray-700 hover:bg-[#20A99E]/5 hover:text-[#20A99E] transition-colors duration-150 font-medium"
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Link
                      to={link.path}
                      className={`text-sm font-semibold tracking-wide uppercase py-3 px-3 rounded-md flex items-center transition-all duration-200 ${location.pathname === link.path
                        ? 'text-[#20A99E] bg-[#20A99E]/10'
                        : `${navTextColorClass} hover:text-[#20A99E] ${isScrolled ? 'hover:bg-[#20A99E]/5' : 'hover:bg-white/10'}`
                        }`}
                    >
                      {link.label}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            {/* Right Icons */}
            <div className="flex items-center space-x-2 md:space-x-3">
              {/* Language Selector */}
              <div className="relative">
                <button
                  onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)}
                  className="hidden sm:flex items-center p-2 min-h-[44px] rounded-md transition-all duration-200 text-gray-600 hover:text-[#20A99E] hover:bg-[#20A99E]/5"
                >
                  <Globe size={20} />
                  <span className="ml-1 text-sm font-medium">{languageNames[language]?.short || 'EN'}</span>
                  <ChevronDown size={14} className={`ml-0.5 transition-transform ${languageDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Language Dropdown */}
                {languageDropdownOpen && (
                  <div className="absolute top-full right-0 mt-1 w-36 bg-white shadow-xl rounded-lg py-1 z-[200] border border-gray-100">
                    {availableLanguages.map((lang) => (
                      <button
                        key={lang}
                        onClick={() => {
                          setLanguage(lang);
                          setLanguageDropdownOpen(false);
                        }}
                        className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${language === lang
                          ? 'bg-[#20A99E]/10 text-[#20A99E] font-medium'
                          : 'text-gray-700 hover:bg-gray-50'
                          }`}
                      >
                        <span className="font-medium">{languageNames[lang]?.full}</span>
                        <span className="text-xs text-gray-400">{languageNames[lang]?.short}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile call button */}
              <a
                href={`tel:${companyInfo.phone}`}
                className="md:hidden flex items-center justify-center p-2 min-h-[44px] min-w-[44px] rounded-md text-[#20A99E]"
              >
                <Phone size={22} />
              </a>

              {/* Mobile menu toggle */}
              <button
                className="lg:hidden p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md transition-colors duration-200 text-gray-700 hover:bg-gray-100"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu - Full Screen Overlay */}
      <div
        className={`lg:hidden fixed inset-0 bg-white z-[105] overflow-y-auto transition-all duration-300 ${mobileMenuOpen
          ? 'opacity-100 translate-x-0'
          : 'opacity-0 translate-x-full pointer-events-none'
          }`}
        style={{ top: isScrolled ? '64px' : '90px' }}
      >
        <nav className="px-5 py-6 space-y-1">
          {translatedNavLinks.map((link, idx) => {
            // Skip Contact Us as it's rendered at the bottom as a special button
            if (link.path === '/contact') return null;

            if (link.submenu) {
              return (
                <div key={idx} className="border-b border-gray-100">
                  <div className="flex items-center justify-between py-4">
                    <Link
                      to={link.path}
                      className="text-base font-bold text-gray-800 hover:text-[#20A99E] transition-colors duration-200"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                    <button
                      onClick={() => setMobileProductsOpen(!mobileProductsOpen)}
                      className="p-2 text-gray-600 hover:text-[#20A99E] active:bg-gray-50 transition-colors duration-200"
                    >
                      <ChevronDown
                        size={20}
                        className={`transition-transform duration-200 ${mobileProductsOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                  </div>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${mobileProductsOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                      }`}
                  >
                    <div className="pb-4 pl-4 space-y-1">
                      {link.submenu.map((item, sIdx) => (
                        <Link
                          key={sIdx}
                          to={item.path}
                          className="flex items-center justify-between py-3 text-sm font-medium text-gray-600 hover:text-[#20A99E] active:bg-gray-50 pr-4 transition-colors duration-200"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <span>{item.label}</span>
                          <ChevronRight size={16} className="text-gray-300" />
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <Link
                key={idx}
                to={link.path}
                className="block py-4 text-base font-bold text-gray-800 hover:text-[#20A99E] border-b border-gray-100 active:bg-gray-50 transition-colors duration-200"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            );
          })}

          {/* Contact Button */}
          <div className="pt-8">
            <Link
              to="/contact"
              className="flex items-center justify-center w-full bg-[#E53935] text-white py-4 rounded-lg text-base font-bold hover:bg-[#D32F2F] transition-all duration-200 active:scale-[0.98] shadow-lg shadow-red-500/20"
              onClick={() => setMobileMenuOpen(false)}
            >
              CONTACT US
            </Link>
          </div>

          {/* Quick Contact Info */}
          <div className="pt-8 space-y-5 pb-10">
            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Connect With Us</div>
            <a href={`tel:${companyInfo.phone}`} className="flex items-center text-sm font-medium text-gray-600 hover:text-[#20A99E] transition-colors duration-200">
              <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center mr-3 border border-gray-100">
                <Phone size={18} className="text-[#20A99E]" />
              </div>
              {companyInfo.phone}
            </a>
            <a href={`mailto:${companyInfo.email}`} className="flex items-center text-sm font-medium text-gray-600 hover:text-[#20A99E] transition-colors duration-200">
              <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center mr-3 border border-gray-100">
                <Mail size={18} className="text-[#20A99E]" />
              </div>
              {companyInfo.email}
            </a>
          </div>
        </nav>
      </div>
    </header >
  );
};

export default Header;
